import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

import { API } from "@/lib/apiBase";
const MAX_ATTEMPTS = 6;
const POLL_INTERVAL_MS = 2000;

const PLAN_LABEL = {
  aficionados: "Aficionados",
  connoisseurs: "Connoisseurs",
};

export default function MembershipSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", attempts: 0 });
  const [detail, setDetail] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error" });
      return;
    }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const r = await axios.get(`${API}/membership/checkout/status/${sessionId}`);
        if (cancelled) return;
        setDetail(r.data);
        if (r.data.payment_status === "paid") {
          setState({ status: "paid" });
          return;
        }
        if (r.data.status === "expired") {
          setState({ status: "expired" });
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState({ status: "timeout" });
          return;
        }
        setState({ status: "polling", attempts: attemptsRef.current });
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (err) {
        console.error(err);
        if (!cancelled) setState({ status: "error" });
      }
    };
    poll();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <main className="pt-24 pb-20 min-h-screen flex items-center justify-center" data-testid="membership-success-page">
      <div className="glass-card rounded-sm p-10 max-w-md w-full mx-6 text-center">
        {state.status === "polling" && (
          <>
            <Loader2 className="w-10 h-10 text-gold/70 mx-auto mb-5 animate-spin" />
            <p className="font-accent text-xs tracking-[0.3em] text-gold/60 mb-3">CONFIRMING</p>
            <h1 className="font-display text-2xl text-white/90 mb-2">Verifying your payment…</h1>
            <p className="font-body text-sm text-white/40">
              This usually takes just a few seconds. Attempt {state.attempts + 1} of {MAX_ATTEMPTS}.
            </p>
          </>
        )}
        {state.status === "paid" && (
          <div data-testid="membership-paid">
            <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-5" />
            <p className="font-accent text-xs tracking-[0.3em] text-gold/60 mb-3">WELCOME</p>
            <h1 className="font-display text-3xl text-white/95 mb-3">Membership Activated</h1>
            <p className="font-body text-sm text-white/50 mb-6">
              Your <span className="text-gold">{PLAN_LABEL[detail?.plan] || detail?.plan}</span> plan ({detail?.interval}) is now active.
              A receipt has been sent to your email.
            </p>
            <div className="flex gap-3 justify-center">
              <Link to="/profile"><Button className="bg-merlot hover:bg-merlot-light text-white">View Profile</Button></Link>
              <Link to="/catalog"><Button variant="outline" className="border-white/15 text-white/80 hover:bg-white/5">Explore Wines</Button></Link>
            </div>
          </div>
        )}
        {(state.status === "expired" || state.status === "timeout" || state.status === "error") && (
          <div data-testid="membership-error">
            <XCircle className="w-10 h-10 text-merlot-light mx-auto mb-5" />
            <p className="font-accent text-xs tracking-[0.3em] text-merlot-light mb-3">NOT CONFIRMED</p>
            <h1 className="font-display text-2xl text-white/90 mb-3">
              {state.status === "expired" ? "Session expired" : state.status === "timeout" ? "Taking longer than expected" : "Could not verify payment"}
            </h1>
            <p className="font-body text-sm text-white/50 mb-6">
              {state.status === "timeout"
                ? "If you completed the payment, please check your email or refresh this page in a moment."
                : "No charge has been made. You can try subscribing again."}
            </p>
            <Link to="/membership">
              <Button className="bg-merlot hover:bg-merlot-light text-white">Back to Plans</Button>
            </Link>
          </div>
        )}
      </div>
    </main>
  );
}
